import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import { useStudioLocale } from '@/i18n/use-studio-locale'

interface RunControlsProps {
  running: boolean
  lastRunAt: number | null
  onRerun: () => void
  disabled?: boolean
}

type RelativeCopy = {
  justNow: string
  secondsAgo: string
  minutesAgo: string
  hoursAgo: string
}

function formatRelative(
  ts: number,
  copy: RelativeCopy,
  now: number = Date.now(),
): string {
  const diffMs = now - ts
  if (diffMs < 1000) return copy.justNow
  if (diffMs < 60_000)
    return copy.secondsAgo.replace('{n}', String(Math.floor(diffMs / 1000)))
  if (diffMs < 3_600_000)
    return copy.minutesAgo.replace('{n}', String(Math.floor(diffMs / 60_000)))
  return copy.hoursAgo.replace('{n}', String(Math.floor(diffMs / 3_600_000)))
}

export function RunControls({ running, lastRunAt, onRerun, disabled }: RunControlsProps) {
  const { strings } = useStudioLocale()
  const copy = strings.designSystem.validatorPanel
  return (
    <div className="flex items-center gap-3">
      {/* `!== null` let `undefined` — and any NaN — through, and the panel
          rendered `Last: NaNh ago`. A timestamp is either a finite number or
          there is nothing to say. */}
      {typeof lastRunAt === 'number' && Number.isFinite(lastRunAt) && (
        <span className="text-xs text-muted-foreground">
          {copy.lastRun} {formatRelative(lastRunAt, copy)}
        </span>
      )}
      <Button
        size="sm"
        variant="outline"
        onClick={onRerun}
        disabled={running || disabled}
      >
        {running ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            {copy.validating}
          </span>
        ) : (
          copy.rerun
        )}
      </Button>
    </div>
  )
}
